'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface ServicesPreviewProps {
  language: 'en' | 'es';
}

const ServicesPreview = ({ language }: ServicesPreviewProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('services-preview');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const services = {
    en: [
      {
        id: 'service_immigration',
        number: '01',
        name: 'Immigration',
        description: 'Investor visas, permanent residency, citizenship, work visas',
      },
      {
        id: 'service_estate',
        number: '02',
        name: 'Estate Planning',
        description: 'Wills, trusts, powers of attorney, succession planning',
      },
      {
        id: 'service_corporate',
        number: '03',
        name: 'Corporate Structuring',
        description: 'Entity optimization, multi-jurisdiction strategies, asset protection',
      },
    ],
    es: [
      {
        id: 'service_immigration',
        number: '01',
        name: 'Inmigración',
        description: 'Visas de inversionista, residencia permanente, ciudadanía, visas de trabajo',
      },
      {
        id: 'service_estate',
        number: '02',
        name: 'Planificación Patrimonial',
        description: 'Testamentos, fideicomisos, poderes legales, planificación sucesoria',
      },
      {
        id: 'service_corporate',
        number: '03',
        name: 'Estructuración Corporativa',
        description: 'Optimización de entidades, estrategias multi-jurisdicción, protección de activos',
      },
    ],
  };

  return (
    <section id="services-preview" className="py-20 px-6">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services[language].map((service, index) => (
            <Link
              key={service.id}
              href="/services"
              className={`group p-8 border hairline border-border sharp hover:border-primary transition-all duration-300 ${
                isVisible ? 'animate-fadeInUp' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span className="font-mono text-6xl font-bold text-muted-foreground group-hover:text-primary transition-colors">
                {service.number}
              </span>
              <h3 className="text-2xl font-bold text-foreground mt-4 mb-2 group-hover:text-primary transition-colors">
                {service.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {service.description}
              </p>
              <div className="mt-4 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;